import { useState, useEffect, useRef, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  Users, Plus, MoreVertical, Pencil, Trash2, Search, AlertCircle, CheckCircle2, XCircle, Shield,
  Upload, FileText, Download, Eye, Image, Paperclip, RefreshCw, FileSpreadsheet, Radio
} from "lucide-react";
import { ImportWizard } from "../components/ImportWizard";
import RadioTelephonyLicenseModal from "../components/pilot/RadioTelephonyLicenseModal";
// Sprint Y Block E - Correspondence Tab
import { CorrespondenceTab } from "../components/correspondence";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

// Default authorization structure
const defaultAuth = { enabled: false, issue_date: "", expiration_date: "" };

export const Pilots = () => {
  const { user } = useAuth();
  const { activeOrganizationId, isPlatformAdmin, registerScopeChangeGuard } = useOrganizationScope();
  const scopedOrganizationId = isPlatformAdmin ? activeOrganizationId : user?.organization_id;
  const { t } = useLanguage();
  const [pilots, setPilots] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [showDocsDialog, setShowDocsDialog] = useState(false);
  const [selectedPilot, setSelectedPilot] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("license");
  const fileInputRef = useRef(null);
  const [editingPilot, setEditingPilot] = useState(null);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showRtModal, setShowRtModal] = useState(false);
  const [rtLicensePilot, setRtLicensePilot] = useState(null);
  const [editingRtLicense, setEditingRtLicense] = useState(null);
  const [formData, setFormData] = useState({
    organization_id: "",
    name: "",
    email: "",
    auth_a1_a3: { ...defaultAuth },
    auth_a2: { ...defaultAuth },
    auth_specific: { ...defaultAuth },
    auth_internal: { ...defaultAuth }
  });

  const fetchData = useCallback(async () => {
    try {
      const [pilotsRes, orgsRes] = await Promise.all([
        api.get('/pilots'),
        api.get('/organizations')
      ]);
      setPilots(pilotsRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      toast.error(t.messages.fetchError);
    } finally {
      setLoading(false);
    }
  }, [t.messages.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!showDialog && !showRtModal) return undefined;
    return registerScopeChangeGuard(() => window.confirm(t.organizationScope?.formDirtyConfirm));
  }, [showDialog, showRtModal, registerScopeChangeGuard, t.organizationScope?.formDirtyConfirm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend validation
    if (!formData.name.trim()) {
      toast.error(t.validation.enterPilotName);
      return;
    }
    
    // Prepare payload with authorization objects
    const payload = {
      organization_id: scopedOrganizationId,
      name: formData.name,
      email: formData.email || null,
      auth_a1_a3: formData.auth_a1_a3.enabled ? {
        enabled: true,
        issue_date: formData.auth_a1_a3.issue_date || null,
        expiration_date: formData.auth_a1_a3.expiration_date || null
      } : { enabled: false, issue_date: null, expiration_date: null },
      auth_a2: formData.auth_a2.enabled ? {
        enabled: true,
        issue_date: formData.auth_a2.issue_date || null,
        expiration_date: formData.auth_a2.expiration_date || null
      } : { enabled: false, issue_date: null, expiration_date: null },
      auth_specific: formData.auth_specific.enabled ? {
        enabled: true,
        issue_date: formData.auth_specific.issue_date || null,
        expiration_date: formData.auth_specific.expiration_date || null
      } : { enabled: false, issue_date: null, expiration_date: null },
      auth_internal: formData.auth_internal.enabled ? {
        enabled: true,
        issue_date: formData.auth_internal.issue_date || null,
        expiration_date: formData.auth_internal.expiration_date || null
      } : { enabled: false, issue_date: null, expiration_date: null }
    };
    
    try {
      if (editingPilot) {
        await api.put(`/pilots/${editingPilot.id}`, payload);
        toast.success(t.messages.pilotUpdated);
      } else {
        await api.post('/pilots', payload);
        toast.success(t.messages.pilotCreated);
      }
      setShowDialog(false);
      setEditingPilot(null);
      resetForm();
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg || e.message || JSON.stringify(e)).join(', '));
      } else {
        toast.error(t.messages.saveError);
      }
    }
  };

  const handleDelete = async (pilot) => {
    if (!window.confirm(`${t.actions.delete} ${pilot.name}?`)) return;
    try {
      await api.delete(`/pilots/${pilot.id}`);
      toast.success(t.messages.pilotDeleted);
      fetchData();
    } catch (error) {
      toast.error(t.messages.deleteError);
    }
  };

  const handleEdit = (pilot) => {
    setEditingPilot(pilot);
    
    // Helper to get auth state, prioritizing new structure over legacy
    const getAuthState = (authObj, legacyExpField, legacyIssueField = null) => {
      // If new auth object exists and has explicit enabled state, use it
      if (authObj !== undefined && authObj !== null && typeof authObj === 'object') {
        return {
          enabled: authObj.enabled === true,
          issue_date: authObj.issue_date?.split("T")[0] || "",
          expiration_date: authObj.expiration_date?.split("T")[0] || ""
        };
      }
      // Only fallback to legacy if auth object doesn't exist
      if (pilot[legacyExpField]) {
        return {
          enabled: true,
          issue_date: legacyIssueField && pilot[legacyIssueField] ? pilot[legacyIssueField].split("T")[0] : "",
          expiration_date: pilot[legacyExpField].split("T")[0]
        };
      }
      return { enabled: false, issue_date: "", expiration_date: "" };
    };
    
    setFormData({
      organization_id: pilot.organization_id,
      name: pilot.name,
      email: pilot.email || "",
      auth_a1_a3: getAuthState(pilot.auth_a1_a3, "certificate_A1_A3_expiration"),
      auth_a2: getAuthState(pilot.auth_a2, "certificate_A2_expiration"),
      auth_specific: getAuthState(pilot.auth_specific, "specific_authorization_expiration"),
      auth_internal: getAuthState(pilot.auth_internal, "internal_training_date")
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      organization_id: scopedOrganizationId || "",
      name: "",
      email: "",
      auth_a1_a3: { ...defaultAuth },
      auth_a2: { ...defaultAuth },
      auth_specific: { ...defaultAuth },
      auth_internal: { ...defaultAuth }
    });
  };

  // Document functions
  const openDocsDialog = (pilot) => {
    setSelectedPilot(pilot);
    setShowDocsDialog(true);
  };

  const handleDocUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedPilot) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        
        await api.post(
          `/pilots/${selectedPilot.id}/documents?document_type=${docType}`,
          formDataUpload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }
      
      // Refresh pilot data
      const res = await api.get(`/pilots/${selectedPilot.id}`);
      setSelectedPilot(res.data);
      
      // Update in list
      setPilots(prev => prev.map(p => p.id === res.data.id ? res.data : p));
      
      toast.success(t.messages?.fileUploaded || "Document uploaded");
    } catch (error) {
      toast.error(t.messages?.uploadError || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await api.get(
        `/pilots/${selectedPilot.id}/documents/${doc.id}`,
        { responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t.messages?.downloadError || "Download failed");
    }
  };

  const deleteDocument = async (doc) => {
    if (!confirm(t.messages?.confirmDelete || "Are you sure?")) return;
    
    try {
      await api.delete(`/pilots/${selectedPilot.id}/documents/${doc.id}`);
      
      setSelectedPilot(prev => ({
        ...prev,
        documents: prev.documents.filter(d => d.id !== doc.id)
      }));
      
      // Update in list
      setPilots(prev => prev.map(p => {
        if (p.id === selectedPilot.id) {
          return { ...p, documents: p.documents?.filter(d => d.id !== doc.id) || [] };
        }
        return p;
      }));
      
      toast.success(t.messages?.fileDeleted || "Document deleted");
    } catch (error) {
      toast.error(t.messages?.deleteError || "Delete failed");
    }
  };

  const isImageFile = (mimeType) => mimeType && mimeType.startsWith("image/");

  const getDocTypeLabel = (type) => {
    const labels = {
      license: t.crew?.license || "License",
      training_certificate: t.crew?.trainingCertificate || "Training Certificate",
      medical: t.crew?.medical || "Medical Certificate",
      other: t.common?.other || "Other"
    };
    return labels[type] || type;
  };

  const updateAuth = (authKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      [authKey]: {
        ...prev[authKey],
        [field]: value
      }
    }));
  };

  const getStatusBadge = (status) => {
    if (status === "valid") return <Badge className="bg-emerald-100 text-emerald-800">{t.crew.valid}</Badge>;
    if (status === "expiring") return <Badge className="bg-amber-100 text-amber-800">{t.crew.expiring}</Badge>;
    return <Badge className="bg-rose-100 text-rose-800">{t.crew.invalid}</Badge>;
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.company_name || "-";
  };

  // Get authorization status for display
  // Priority: new auth object > only use legacy if auth object doesn't exist
  const getAuthStatus = (pilot, authField, legacyField) => {
    const auth = pilot[authField];
    
    // If the new authorization object exists, use it as source of truth
    if (auth !== undefined && auth !== null) {
      // Check if explicitly disabled
      if (auth.enabled === false) {
        return { enabled: false, expiration: null };
      }
      // Check if enabled
      if (auth.enabled === true) {
        return { enabled: true, expiration: auth.expiration_date };
      }
    }
    
    // Only fallback to legacy field if new auth object doesn't exist at all
    // This handles pilots created before the new auth structure
    if (auth === undefined && pilot[legacyField]) {
      return { enabled: true, expiration: pilot[legacyField] };
    }
    
    return { enabled: false, expiration: null };
  };

  const AuthBadge = ({ enabled, expiration }) => {
    if (!enabled) return <XCircle className="h-4 w-4 text-slate-300" />;
    
    if (expiration) {
      const expDate = new Date(expiration);
      const now = new Date();
      const daysUntil = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysUntil < 0) return <XCircle className="h-4 w-4 text-rose-500" title={t.crew.expired} />;
      if (daysUntil < 60) return <AlertCircle className="h-4 w-4 text-amber-500" title={`${t.crew.expiring} - ${daysUntil} days`} />;
    }
    return <CheckCircle2 className="h-4 w-4 text-emerald-500" title={t.crew.valid} />;
  };

  // Wave 2B: RT License status badge
  const getRtStatusBadge = (pilot) => {
    const rtStatus = pilot.rt_status;
    const trRt = t.radioTelephony?.status || {};
    
    if (rtStatus === "valid") {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs gap-1">
          <Radio className="h-3 w-3" />
          {trRt.valid || "Valid"}
        </Badge>
      );
    }
    if (rtStatus === "expiring_soon") {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs gap-1">
          <Radio className="h-3 w-3" />
          {trRt.expiringSoon || "Expiring"}
        </Badge>
      );
    }
    if (rtStatus === "expired") {
      return (
        <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-xs gap-1">
          <Radio className="h-3 w-3" />
          {trRt.expired || "Expired"}
        </Badge>
      );
    }
    // none or undefined
    return (
      <Badge variant="outline" className="text-slate-400 border-slate-200 text-xs">
        <Radio className="h-3 w-3 mr-1" />
        {trRt.none || "-"}
      </Badge>
    );
  };

  const filteredPilots = pilots.filter(pilot =>
    pilot.name.toLowerCase().includes(search.toLowerCase()) ||
    (pilot.email && pilot.email.toLowerCase().includes(search.toLowerCase()))
  );

  const expiringCount = pilots.filter(p => p.pilot_status === "expiring").length;
  const invalidCount = pilots.filter(p => p.pilot_status === "invalid").length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pilots-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {t.crew.pilots}
          </h1>
          <p className="text-slate-500 mt-1">{t.pageDescriptions.pilots}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowImportWizard(true)}
            className="gap-2"
            data-testid="import-pilots-btn"
          >
            <FileSpreadsheet className="h-4 w-4" /> {t.actions?.import || 'Import'}
          </Button>
          <Button 
            onClick={() => { resetForm(); setEditingPilot(null); setShowDialog(true); }}
            className="gap-2 bg-primary hover:bg-primary/90"
            data-testid="add-pilot-btn"
          >
            <Plus className="h-4 w-4" /> {t.crew.addPilot}
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {(expiringCount > 0 || invalidCount > 0) && (
        <div className="flex gap-4">
          {invalidCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-rose-50 border border-rose-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-rose-500" />
              <span className="text-sm text-rose-700">{invalidCount} {t.crew.pilotsExpiredAlert}</span>
            </div>
          )}
          {expiringCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <span className="text-sm text-amber-700">{expiringCount} {t.crew.pilotsExpiringAlert}</span>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t.crew.searchPilots}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-pilots"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.crew.pilotName}</TableHead>
                {user?.role === "platform_admin" && <TableHead>{t.common.organization}</TableHead>}
                <TableHead>{t.crew.pilotEmail}</TableHead>
                <TableHead className="text-center">A1/A3</TableHead>
                <TableHead className="text-center">A2</TableHead>
                <TableHead className="text-center">{t.crew.authSpecific.split(' ')[0]}</TableHead>
                <TableHead className="text-center">{t.crew.authInternal.split(' ')[0]}</TableHead>
                <TableHead className="text-center">RT</TableHead>
                <TableHead>{t.common.status}</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : filteredPilots.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">{t.crew.noPilotsFound}</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPilots.map((pilot) => {
                  const a1a3 = getAuthStatus(pilot, "auth_a1_a3", "certificate_A1_A3_expiration");
                  const a2 = getAuthStatus(pilot, "auth_a2", "certificate_A2_expiration");
                  const specific = getAuthStatus(pilot, "auth_specific", "specific_authorization_expiration");
                  const internal = getAuthStatus(pilot, "auth_internal", "internal_training_date");
                  
                  return (
                    <TableRow key={pilot.id} data-testid={`pilot-row-${pilot.id}`}>
                      <TableCell className="font-medium">{pilot.name}</TableCell>
                      {user?.role === "platform_admin" && (
                        <TableCell className="text-sm text-slate-500">{getOrgName(pilot.organization_id)}</TableCell>
                      )}
                      <TableCell className="text-sm">{pilot.email || "-"}</TableCell>
                      <TableCell className="text-center">
                        <AuthBadge enabled={a1a3.enabled} expiration={a1a3.expiration} />
                      </TableCell>
                      <TableCell className="text-center">
                        <AuthBadge enabled={a2.enabled} expiration={a2.expiration} />
                      </TableCell>
                      <TableCell className="text-center">
                        <AuthBadge enabled={specific.enabled} expiration={specific.expiration} />
                      </TableCell>
                      <TableCell className="text-center">
                        <AuthBadge enabled={internal.enabled} expiration={internal.expiration} />
                      </TableCell>
                      <TableCell className="text-center">
                        {getRtStatusBadge(pilot)}
                      </TableCell>
                      <TableCell>{getStatusBadge(pilot.pilot_status)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`pilot-actions-${pilot.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(pilot)}>
                              <Pencil className="h-4 w-4 mr-2" /> {t.actions.edit}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openDocsDialog(pilot)}>
                              <FileText className="h-4 w-4 mr-2" /> {t.crew?.documents || "Documents"}
                              {pilot.documents?.length > 0 && (
                                <Badge variant="secondary" className="ml-2">{pilot.documents.length}</Badge>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(pilot)} className="text-red-600">
                              <Trash2 className="h-4 w-4 mr-2" /> {t.actions.delete}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPilot ? t.dialogs.editPilot : t.dialogs.addPilot}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="pilot-form">
            {user?.role === "platform_admin" && (
              <div className="space-y-2">
                <Label>{t.common.organization} *</Label>
                <Select
                  value={formData.organization_id}
                  disabled
                  onValueChange={(value) => setFormData({ ...formData, organization_id: value })}
                >
                  <SelectTrigger data-testid="pilot-organization">
                    <SelectValue placeholder={t.placeholders.selectOrganization} />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pilot_name">{t.crew.pilotName} *</Label>
                <Input
                  id="pilot_name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="pilot-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pilot_email">{t.crew.pilotEmail}</Label>
                <Input
                  id="pilot_email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="pilot-email"
                />
              </div>
            </div>

            {/* Authorization Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b">
                <Shield className="h-5 w-5 text-slate-600" />
                <h3 className="font-semibold text-slate-700">{t.crew.authorizations}</h3>
              </div>
              
              {/* A1/A3 Authorization */}
              <div className="p-4 rounded-lg border bg-slate-50/50 space-y-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="auth_a1_a3"
                    checked={formData.auth_a1_a3.enabled}
                    onCheckedChange={(checked) => updateAuth("auth_a1_a3", "enabled", checked)}
                    data-testid="auth-a1a3-checkbox"
                  />
                  <Label htmlFor="auth_a1_a3" className="font-medium cursor-pointer">
                    {t.crew.authA1A3}
                  </Label>
                </div>
                {formData.auth_a1_a3.enabled && (
                  <div className="grid grid-cols-2 gap-4 pl-7">
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">{t.crew.issueDate}</Label>
                      <Input
                        type="date"
                        value={formData.auth_a1_a3.issue_date}
                        onChange={(e) => updateAuth("auth_a1_a3", "issue_date", e.target.value)}
                        data-testid="auth-a1a3-issue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">{t.crew.expirationDate}</Label>
                      <Input
                        type="date"
                        value={formData.auth_a1_a3.expiration_date}
                        onChange={(e) => updateAuth("auth_a1_a3", "expiration_date", e.target.value)}
                        data-testid="auth-a1a3-expiration"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* A2 Authorization */}
              <div className="p-4 rounded-lg border bg-slate-50/50 space-y-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="auth_a2"
                    checked={formData.auth_a2.enabled}
                    onCheckedChange={(checked) => updateAuth("auth_a2", "enabled", checked)}
                    data-testid="auth-a2-checkbox"
                  />
                  <Label htmlFor="auth_a2" className="font-medium cursor-pointer">
                    {t.crew.authA2}
                  </Label>
                </div>
                {formData.auth_a2.enabled && (
                  <div className="grid grid-cols-2 gap-4 pl-7">
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">{t.crew.issueDate}</Label>
                      <Input
                        type="date"
                        value={formData.auth_a2.issue_date}
                        onChange={(e) => updateAuth("auth_a2", "issue_date", e.target.value)}
                        data-testid="auth-a2-issue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">{t.crew.expirationDate}</Label>
                      <Input
                        type="date"
                        value={formData.auth_a2.expiration_date}
                        onChange={(e) => updateAuth("auth_a2", "expiration_date", e.target.value)}
                        data-testid="auth-a2-expiration"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Specific Category Authorization */}
              <div className="p-4 rounded-lg border bg-slate-50/50 space-y-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="auth_specific"
                    checked={formData.auth_specific.enabled}
                    onCheckedChange={(checked) => updateAuth("auth_specific", "enabled", checked)}
                    data-testid="auth-specific-checkbox"
                  />
                  <Label htmlFor="auth_specific" className="font-medium cursor-pointer">
                    {t.crew.authSpecific}
                  </Label>
                </div>
                {formData.auth_specific.enabled && (
                  <div className="grid grid-cols-2 gap-4 pl-7">
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">{t.crew.issueDate}</Label>
                      <Input
                        type="date"
                        value={formData.auth_specific.issue_date}
                        onChange={(e) => updateAuth("auth_specific", "issue_date", e.target.value)}
                        data-testid="auth-specific-issue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">{t.crew.expirationDate}</Label>
                      <Input
                        type="date"
                        value={formData.auth_specific.expiration_date}
                        onChange={(e) => updateAuth("auth_specific", "expiration_date", e.target.value)}
                        data-testid="auth-specific-expiration"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Internal Company Authorization */}
              <div className="p-4 rounded-lg border bg-slate-50/50 space-y-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="auth_internal"
                    checked={formData.auth_internal.enabled}
                    onCheckedChange={(checked) => updateAuth("auth_internal", "enabled", checked)}
                    data-testid="auth-internal-checkbox"
                  />
                  <Label htmlFor="auth_internal" className="font-medium cursor-pointer">
                    {t.crew.authInternal}
                  </Label>
                </div>
                {formData.auth_internal.enabled && (
                  <div className="grid grid-cols-2 gap-4 pl-7">
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">{t.crew.trainingDate}</Label>
                      <Input
                        type="date"
                        value={formData.auth_internal.issue_date}
                        onChange={(e) => updateAuth("auth_internal", "issue_date", e.target.value)}
                        data-testid="auth-internal-issue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">{t.crew.expirationOptional}</Label>
                      <Input
                        type="date"
                        value={formData.auth_internal.expiration_date}
                        onChange={(e) => updateAuth("auth_internal", "expiration_date", e.target.value)}
                        data-testid="auth-internal-expiration"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Wave 2B: Radio Telephony Licenses Section */}
            {editingPilot && (
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b">
                  <div className="flex items-center gap-2">
                    <Radio className="h-5 w-5 text-slate-600" />
                    <h3 className="font-semibold text-slate-700">{t.radioTelephony?.title || "Radio Telephony Licenses"}</h3>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRtLicensePilot(editingPilot);
                      setEditingRtLicense(null);
                      setShowRtModal(true);
                    }}
                    data-testid="add-rt-license-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t.radioTelephony?.addLicense || "Add RT License"}
                  </Button>
                </div>

                {/* RT Licenses List */}
                {(editingPilot.radio_telephony_licenses?.length > 0) ? (
                  <div className="space-y-2">
                    {editingPilot.radio_telephony_licenses.map((lic) => {
                      const isExpired = lic.expires_at && new Date(lic.expires_at) < new Date();
                      const isExpiring = lic.expires_at && 
                        new Date(lic.expires_at) > new Date() && 
                        new Date(lic.expires_at) < new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
                      
                      return (
                        <div
                          key={lic.id}
                          className={`p-3 rounded-lg border flex items-center justify-between ${
                            isExpired ? "bg-rose-50 border-rose-200" :
                            isExpiring ? "bg-amber-50 border-amber-200" :
                            "bg-emerald-50 border-emerald-200"
                          }`}
                          data-testid={`rt-license-${lic.id}`}
                        >
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{lic.license_type}</span>
                              <span className="text-sm text-slate-500">#{lic.license_number}</span>
                            </div>
                            <div className="text-xs text-slate-500">
                              {lic.issuer} | {t.radioTelephony?.expiresAt || "Expires"}: {lic.expires_at?.split("T")[0] || "-"}
                              {" | "}{(lic.languages || []).join(", ")}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={`text-xs ${
                              isExpired ? "bg-rose-100 text-rose-700" :
                              isExpiring ? "bg-amber-100 text-amber-700" :
                              "bg-emerald-100 text-emerald-700"
                            }`}>
                              {isExpired ? (t.radioTelephony?.status?.expired || "Expired") :
                               isExpiring ? (t.radioTelephony?.status?.expiringSoon || "Expiring") :
                               (t.radioTelephony?.status?.valid || "Valid")}
                            </Badge>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setRtLicensePilot(editingPilot);
                                setEditingRtLicense(lic);
                                setShowRtModal(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-rose-500 hover:text-rose-700"
                              onClick={async () => {
                                if (!window.confirm(t.radioTelephony?.confirmDelete || "Delete this RT license?")) return;
                                try {
                                  await api.delete(`/pilots/${editingPilot.id}/radio-telephony/${lic.id}`);
                                  toast.success(t.radioTelephony?.licenseDeleted || "RT license deleted");
                                  fetchData();
                                  // Update local state
                                  setEditingPilot(prev => ({
                                    ...prev,
                                    radio_telephony_licenses: prev.radio_telephony_licenses.filter(l => l.id !== lic.id)
                                  }));
                                } catch (error) {
                                  toast.error(t.radioTelephony?.deleteFailed || "Failed to delete license");
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 rounded-lg border border-dashed bg-slate-50 text-center text-sm text-slate-500">
                    <Radio className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    <p>{t.radioTelephony?.noLicenses || "No RT licenses added yet"}</p>
                    <p className="text-xs mt-1">{t.radioTelephony?.hint || "Required for operations in controlled airspace (CTR/ATZ)"}</p>
                  </div>
                )}
              </div>
            )}

            {/* Sprint Y Block E: Correspondence Tab for Pilot */}
            {editingPilot && (
              <div className="space-y-4 border-t pt-4">
                <CorrespondenceTab
                  entityType="pilot"
                  entityId={editingPilot.id}
                  includeProposed={true}
                  compact={true}
                  maxItems={5}
                />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>{t.actions.cancel}</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="pilot-submit-btn">
                {editingPilot ? t.actions.update : t.actions.create}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={showDocsDialog} onOpenChange={setShowDocsDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t.crew?.documents || "Documents"} - {selectedPilot?.name}
            </DialogTitle>
          </DialogHeader>
          
          {/* Upload Section */}
          <div className="border-b pb-4 mb-4">
            <div className="flex items-center gap-4">
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="license">{t.crew?.license || "License"}</SelectItem>
                  <SelectItem value="training_certificate">{t.crew?.trainingCertificate || "Training Certificate"}</SelectItem>
                  <SelectItem value="medical">{t.crew?.medical || "Medical Certificate"}</SelectItem>
                  <SelectItem value="other">{t.common?.other || "Other"}</SelectItem>
                </SelectContent>
              </Select>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleDocUpload}
                className="hidden"
                multiple
                accept="image/*,.pdf,.doc,.docx"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="gap-2"
              >
                {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {t.actions?.upload || "Upload"}
              </Button>
            </div>
          </div>

          {/* Documents List */}
          {(!selectedPilot?.documents || selectedPilot.documents.length === 0) ? (
            <div className="text-center py-8 text-slate-500">
              <Paperclip className="h-10 w-10 mx-auto mb-2 text-slate-300" />
              <p>{t.crew?.noDocuments || "No documents uploaded"}</p>
              <p className="text-xs mt-1">{t.crew?.uploadHint || "Upload license scans, training certificates, or other documents"}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedPilot.documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-3 min-w-0">
                    {isImageFile(doc.mime_type) ? (
                      <Image className="h-8 w-8 text-blue-500 flex-shrink-0" />
                    ) : (
                      <FileText className="h-8 w-8 text-slate-500 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{doc.file_name}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">{getDocTypeLabel(doc.document_type)}</Badge>
                        <span className="text-xs text-slate-500">
                          {new Date(doc.upload_timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadDocument(doc)}
                      className="h-8 w-8 p-0"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteDocument(doc)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Wizard */}
      <ImportWizard
        open={showImportWizard}
        onOpenChange={setShowImportWizard}
        entityType="pilots"
        onComplete={() => fetchData()}
      />

      {/* Wave 2B: Radio Telephony License Modal */}
      <RadioTelephonyLicenseModal
        open={showRtModal}
        onOpenChange={setShowRtModal}
        pilotId={rtLicensePilot?.id}
        license={editingRtLicense}
        onSuccess={() => {
          fetchData();
          // Refresh editingPilot with updated RT licenses
          if (rtLicensePilot) {
            api.get(`/pilots/${rtLicensePilot.id}`).then(res => {
              setEditingPilot(res.data);
            });
          }
        }}
      />
    </div>
  );
};
